import React from 'react';
import { usePageSeo } from '../hooks/usePageSeo';
import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';

const PrivacyPolicy = () => {
  usePageSeo({
    title: 'Privacy Policy | Civic Rentals LLC',
    description: 'Civic Rentals LLC Privacy Policy describing how we collect, use, disclose, and protect personal information in connection with our website, platform, and services.',
    canonical: 'https://civicrentals.com/privacypolicy',
    ogTitle: 'Privacy Policy | Civic Rentals LLC',
    ogDescription: 'Civic Rentals LLC Privacy Policy describing how we collect, use, disclose, and protect personal information.',
    ogUrl: 'https://civicrentals.com/privacypolicy',
    ogImage: 'https://civicrentals.com/favicon.png',
    twitterCard: 'summary',
  });

  const sectionTitle = "text-xl font-bold text-slate-900 mt-8 mb-3";
  const subTitle = "text-lg font-semibold text-slate-800 mt-6 mb-2";
  const para = "text-slate-700 leading-relaxed mb-4";

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#d5e0de' }}>
      <Header />

      <section className="pt-32 pb-16 px-6">
        <div className="container mx-auto max-w-3xl">
          {/* Breadcrumbs */}
          <nav aria-label="Breadcrumb" className="mb-6" data-testid="breadcrumbs">
            <ol className="flex items-center gap-1.5 text-sm text-slate-500">
              <li><Link to="/" className="hover:text-slate-800 transition-colors">Home</Link></li>
              <li><ChevronRight className="h-3.5 w-3.5" /></li>
              <li className="text-slate-700 font-medium">Privacy Policy</li>
            </ol>
          </nav>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="px-8 py-8 sm:px-10 sm:py-10">
              <h1 className="text-3xl font-bold text-slate-900 mb-2" data-testid="privacy-policy-title">CIVIC RENTALS LLC</h1>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">PRIVACY POLICY</h2>
              <p className="text-sm text-slate-500 mb-8">Last updated: November 2, 2026</p>

              <p className={para}>
                Civic Rentals LLC ("Civic Rentals," "Civic Rentals Software," "we," "us," or "our") provides software and technology services to government agencies. This Privacy Policy describes how we collect, use, disclose, and protect personal information in connection with our website, platform, and services.
              </p>

              {/* 1. Scope */}
              <h3 className={sectionTitle}>1. Scope of This Policy</h3>
              <p className={para}>
                This Privacy Policy applies to personal information that we collect and process in connection with our business operations and services.
              </p>
              <p className={para}>
                When processing personal information on behalf of government customers, Civic Rentals processes that information only on the documented instructions of that customer and for that customer's purposes, subject to its agreements with those customers and applicable law. Government customers are responsible for determining how information under their control is used and managed.
              </p>
              <p className={para}>
                Before a contract is signed, we may independently collect information from publicly available sources and third-party data providers and analyze it at an aggregate level for business purposes, including evaluating potential services and preparing proposals for prospective government customers. Once a contract is signed, we become the data processor on behalf of the government customer.
              </p>
              <p className={para}>
                For questions relating to information maintained by a government agency, please contact the relevant agency directly.
              </p>

              {/* 2. Information We Collect */}
              <h3 className={sectionTitle}>2. Information We Collect</h3>
              <p className={para}>
                We may collect personal information that you provide to us, including contact details, professional information, communications, documents, and information submitted through our services. Depending on the services used, this may include sensitive personal information, such as government-issued identification information, or other information provided in connection with government programs and services. We use sensitive personal information only for purposes permitted under applicable law, including providing the services our customers request, securing our systems, and meeting legal obligations. We do not use or disclose sensitive personal information to infer characteristics about any individual.
              </p>
              <p className={para}>The categories of personal information we may collect include:</p>

              <h4 className={subTitle}>Video, Image, Electronic, or Similar Information</h4>
              <p className={para}>
                We may collect site plans, photographs, electronic files, videos, pdf attachments, and similar information from you when requested by our customers as part of their services and forms.
              </p>

              <h4 className={subTitle}>Authentication Information</h4>
              <p className={para}>
                For users with accounts, we may collect authentication credentials to administer and secure access to our systems and services.
              </p>

              <h4 className={subTitle}>Browser and Website Activity Information</h4>
              <p className={para}>
                We may automatically collect browser information, including browser user agents, device information, website browsing activity, pages viewed, sessions, forms submitted, and associated timestamps.
              </p>
              <p className={para}>
                We use this information for internal diagnostics, support, analytics, and improving our services.
              </p>
              <p className={para}>
                We use cookies and similar technologies, including Google Analytics, to understand how visitors interact with our website, analyze website traffic, and improve our services. Where applicable, visitors may manage their cookie preferences, including opting out of non-essential analytics cookies, through the options available on our website. Google may collect information about your use of our website as described in Google's privacy policy. Our website does not currently respond to browser Do Not Track signals.
              </p>

              <h4 className={subTitle}>Identifiers and Communications</h4>
              <p className={para}>
                We may collect names, email addresses, phone numbers, ZIP codes, physical and/or mailing addresses, IP addresses, preferred languages, and form responses.
              </p>
              <p className={para}>
                We use this information to deliver messages to government officials who have expressed interest in learning about our services, and/or to provide customer technical support services to government customers who use our system. IP addresses may also be used to estimate approximate geographic location.
              </p>
              <p className={para}>
                We may also use this information to deliver messages on behalf of our government customers, facilitate registrations such as permits and licensing, administer services, and conduct internal auditing, diagnostics, support, and troubleshooting.
              </p>

              <h4 className={subTitle}>How We Use Information</h4>
              <p className={para}>
                We use the information described above to operate, provide, secure, maintain, analyze, and improve our services to customers, communicate on behalf of customers, communicate to customers, respond to inquiries, and comply with applicable legal obligations.
              </p>
              <p className={para}>
                Where information is processed on behalf of government customers, it is handled in accordance with our agreements with those customers and applicable law.
              </p>

              {/* 3. Platform Users */}
              <h3 className={sectionTitle}>3. Platform Users</h3>
              <p className={para}>
                We may collect and process information relating to individuals who access or use our platform, including account information, contact details, and usage information.
              </p>
              <p className={para}>
                We use this information to provide and administer our services, manage accounts, communicate with users, provide support, maintain security, and fulfill our contractual and legal obligations.
              </p>

              {/* 4. Payments */}
              <h3 className={sectionTitle}>4. Payments</h3>
              <p className={para}>
                Payments made through our services may be processed by third-party payment providers. Information associated with these transactions is handled in accordance with applicable agreements, laws, and the privacy practices of the relevant providers. Payments and related financial services made available through our platform may be facilitated by third-party providers, such as Stripe. Payment and financial information is processed in accordance with applicable agreements, laws, and the privacy practices of those providers.
              </p>

              {/* 5. Information Collected from Other Sources */}
              <h3 className={sectionTitle}>5. Information Collected from Other Sources</h3>
              <p className={para}>
                We may obtain information from publicly available sources, third-party data providers, government agencies, and other sources in connection with providing and improving our services.
              </p>
              <p className={para}>
                We use this information for business purposes, including analyzing a short-term rental landscape, providing data and technology services, and supporting government customers in administering their programs and enforcing applicable regulations.
              </p>
              <p className={para}>
                We provide the platform only to government customers for use in administering their own rental programs. We do not sell or share personal information for advertising or marketing, and we do not make personal information available for third-party commercial use.
              </p>
              <p className={para}>
                We may disclose information to government customers, service providers, and other parties as described in this Privacy Policy.
              </p>
              <p className={para}>
                When we process information on behalf of government customers, our handling of that information is subject to applicable agreements and legal requirements. Requests concerning government-controlled records should be directed to the relevant government agency.
              </p>
              <p className={para}>
                Information obtained from third-party sources may be incomplete or inaccurate. Government customers remain responsible for their own regulatory and enforcement decisions.
              </p>

              {/* 6. Sale and Sharing */}
              <h3 className={sectionTitle}>6. Sale and Sharing of Personal Information</h3>
              <p className={para}>
                We do not sell personal information or share personal information for cross-context behavioral advertising, as those terms are defined under applicable privacy laws. Because we do not sell or share personal information, no opt-out is required. If you believe a disclosure we make should be treated as a sale or sharing under the law that applies to you, contact us at <a href="mailto:privacy@civicrentals.com" className="text-blue-600 hover:text-blue-800 underline">privacy@civicrentals.com</a> and we will review it.
              </p>
              <p className={para}>
                We may disclose personal information to government customers, service providers, and other parties for the purposes described in this Privacy Policy, subject to applicable law and contractual requirements.
              </p>

              {/* 7. Your Privacy Rights */}
              <h3 className={sectionTitle}>7. Your Privacy Rights</h3>
              <p className={para}>
                Depending on where you reside, applicable privacy laws may provide you with certain rights regarding your personal information. These may include the right to request access to, correction of, or deletion of your personal information, and the right to opt out of certain processing activities, including the sale or sharing of personal information, targeted advertising, or profiling.
              </p>
              <p className={para}>
                These rights are subject to applicable legal requirements and exceptions.
              </p>
              <p className={para}>
                To exercise your rights, please contact us at <a href="mailto:privacy@civicrentals.com" className="text-blue-600 hover:text-blue-800 underline">privacy@civicrentals.com</a>. We may request additional information to verify your identity or authority to make a request. Where permitted by law, you may designate an authorized agent to act on your behalf. If you are an Oregon resident, you may also request a list of the specific third parties to which we have disclosed personal information.
              </p>
              <p className={para}>
                If we decline to act on your request, you may have the right to appeal our decision by contacting us using the email address above.
              </p>
              <p className={para}>
                We will review and respond to requests and appeals in accordance with applicable privacy laws.
              </p>

              {/* 8. Retention */}
              <h3 className={sectionTitle}>8. How Long We Keep Information</h3>
              <p className={para}>
                We retain personal information for as long as it is needed for the purpose it was collected for. In setting that period we consider how long the information is needed to provide our services, what our agreements with government customers require, what those customers' own records retention laws require, and any period required by law or needed to establish or defend legal claims.
              </p>
              <p className={para}>
                When information is no longer needed, we will delete, anonymize, or otherwise handle it in accordance with applicable legal requirements.
              </p>

              {/* 9. Service Providers */}
              <h3 className={sectionTitle}>9. Service Providers, Contractors, and International Access</h3>
              <p className={para}>
                We engage third-party service providers and independent contractors to support our business operations and services. These providers may include cloud hosting and infrastructure providers, database and data storage providers, identity and authentication services, payment and financial services providers, printing, mailing and communications providers, analytics services, security providers, software development contractors, and technical support providers.
              </p>
              <p className={para}>
                These providers may access personal information only as reasonably necessary to perform authorized services on our behalf. We require appropriate contractual, confidentiality, and security safeguards designed to protect personal information and restrict its use and disclosure, including unauthorized use for artificial intelligence model training.
              </p>
              <p className={para}>
                Certain authorized service providers and contractors may access personal information from locations outside the United States. Such access is subject to applicable law, contractual obligations, and appropriate security measures.
              </p>
              <p className={para}>
                We take reasonable steps to limit access to personal information based on business needs and to protect it against unauthorized access, use, or disclosure.
              </p>
              <p className={para}>
                We may also disclose personal information when required or permitted by law, including in response to lawful requests from government authorities, courts, or regulators; to protect our legal rights; or in connection with a merger, acquisition, reorganization, or sale of company assets, subject to applicable legal requirements.
              </p>

              {/* 10. Security */}
              <h3 className={sectionTitle}>10. Security</h3>
              <p className={para}>
                We have implemented reasonable technical and organizational measures designed to protect the personal information we process. However, no method of electronic transmission or storage is completely secure, and we cannot guarantee absolute security.
              </p>

              {/* 11. Children */}
              <h3 className={sectionTitle}>11. Children</h3>
              <p className={para}>
                Our services are not directed to children under 16, and we do not knowingly collect personal information from children under 16. If you believe we have collected such information, please contact us at <a href="mailto:privacy@civicrentals.com" className="text-blue-600 hover:text-blue-800 underline">privacy@civicrentals.com</a>.
              </p>

              {/* 12. Third-Party Websites */}
              <h3 className={sectionTitle}>12. Third-Party Websites</h3>
              <p className={para}>
                Our website and services may contain links to third-party websites or services that are not operated or controlled by Civic Rentals.
              </p>
              <p className={para}>
                These third parties may have their own privacy policies and practices. We are not responsible for the content or privacy practices of third-party websites or services, and we encourage users to review their privacy policies before providing personal information.
              </p>

              {/* 13. AI */}
              <h3 className={sectionTitle}>13. Artificial Intelligence and Automated Technologies</h3>
              <p className={para}>
                We may use artificial intelligence, machine learning, and other automated technologies to support our business operations, develop and improve our software, analyze information, and provide our services.
              </p>
              <p className={para}>
                These technologies may assist with data analysis, identifying patterns, and generating insights. We may also use third-party AI service providers, subject to applicable contractual, privacy, and security requirements. We do not use personal information provided by, or collected on behalf of, our government customers to train artificial intelligence or machine learning models that serve other customers.
              </p>
              <p className={para}>
                Where personal information is processed using these technologies, we handle it in accordance with this Privacy Policy, applicable law, and our agreements with government customers.
              </p>

              {/* 14. Changes */}
              <h3 className={sectionTitle}>14. Changes to This Policy</h3>
              <p className={para}>
                We may update this Privacy Policy from time to time. Any updates will be reflected by the revised date posted with this Policy. We will provide additional notice where required by law.
              </p>

              {/* 15. Contact Us */}
              <h3 className={sectionTitle}>15. Contact Us</h3>
              <p className={para}>
                If you have questions or concerns about this Privacy Policy, you may contact us at:
              </p>
              <div className="text-slate-700 leading-relaxed mb-4">
                <p className="font-semibold">Civic Rentals LLC</p>
                <p>PO Box 8</p>
                <p>Conway, NH 03818</p>
                <p className="mt-2">
                  <a href="mailto:privacy@civicrentals.com" className="text-blue-600 hover:text-blue-800 underline">privacy@civicrentals.com</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-slate-900 text-slate-400 py-12 px-6 border-t border-slate-800">
        <div className="container mx-auto max-w-6xl">
          <div className="border-t border-slate-800 pt-8 text-center text-sm">
            <p>&copy; 2026 Civic Rentals LLC. All rights reserved.</p>
            <p className="mt-2 space-x-4">
              <a href="/privacypolicy" className="hover:text-white transition-colors">Privacy Policy</a>
              <a href="/websiteTOS" className="hover:text-white transition-colors">Terms of Service</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
